import { CircularProgress } from "@mui/material";
import { type FC, useCallback, useMemo } from "react";
import { type Block, Message } from "slack-blocks-to-jsx";
import "slack-blocks-to-jsx/dist/style.css";
import type { SlackEngineV2Message } from "../../../../../../../../server/api/slack-engine-v2";
import { useSupabaseLoader } from "../../../../../../../../server/supabase/hooks";
import { SLACK_INVISIBLE_CHARACTER } from "../../../../../../../message/editor/utils/markdown-transformers/slack-rich-text-transform";
import { SupabaseCustomEmojiService } from "../../../../../../../slack/server/slack-supabase-service";
import "./slack-wrapper.css";

export type PreviewSlackContext = {
  channels?: {
    id: string;
    name: string;
  }[];
};

type LinkInputFn = Required<Required<Parameters<typeof Message>[0]>["hooks"]>["link"];

type Emoji = {
  name: string;
  unicode?: string;
  skin_tone?: 1 | 2 | 3 | 4 | 5 | 6;
};

export const SlackWrapper: FC<{
  data: SlackEngineV2Message["data"];
  context?: PreviewSlackContext;
  aliasName?: string;
  aliasImage?: string;
}> = ({ data, context = {}, aliasName, aliasImage }) => {
  const handleLinkClick: LinkInputFn = useCallback((input) => {
    return (
      <a href={input.href} target="_blank" rel="noopener noreferrer" className="slack_link">
        {input.children}
      </a>
    );
  }, []);

  const usedEmoji = useMemo(() => {
    const tmp: string[] = [];
    recurse(
      data,
      (k, v) => k === "type" && v === "emoji",
      (o) => tmp.push((o as { name: string }).name),
    );
    return tmp;
  }, [data]);

  const { data: customEmojis, loading } = useSupabaseLoader(
    ({ supabase, account_id }) =>
      new SupabaseCustomEmojiService(supabase).getByNames(account_id, usedEmoji),
    [usedEmoji],
  );

  const emojiHook = useCallback(
    (findEmoji: Emoji, parse: (data: Emoji) => string) => {
      const customEmoji = customEmojis?.data!.find((emoji) => emoji.emoji === findEmoji.name);
      if (customEmoji) {
        return (
          <img
            src={customEmoji.url}
            alt={`:${findEmoji.name}:`}
            title={findEmoji.name}
            style={{
              width: "1em",
              height: "1em",
              objectFit: "contain",
              verticalAlign: "middle",
              display: "inline-block",
            }}
          />
        );
      }
      const e = parse(findEmoji);
      return /^\w+$/.test(e) ? `:${e}:` : e;
    },
    [customEmojis],
  );

  if (loading) return <CircularProgress />;
  return (
    <div className="slack-preview-capture-area">
      <Message
        time={new Date()}
        name={aliasName ?? "ChangeEngine"}
        logo={aliasImage ?? "https://images.changeengine.com/ce-logo.png"}
        blocks={data.map(blockToSlackBlock)}
        hooks={{ link: handleLinkClick, emoji: emojiHook }}
        style={{ color: "black" }}
        data={context}
      />
    </div>
  );
};

function blockToSlackBlock(block: SlackEngineV2Message["data"][number]): Block {
  if (block.type === "section")
    return {
      ...block,
      text: fixMrkdwnOrTextType(block.text),
    };

  if (block.type === "actions")
    return {
      ...block,
      elements: block.elements.map((element) => ({
        ...element,
        text: {
          type: "plain_text" as const,
          text: element.text.text,
        },
      })),
    };

  if (block.type === "rich_text") {
    const str = JSON.stringify(block);
    // Replace invisible character with a space to ensure the block takes up a new line
    return JSON.parse(str.replaceAll(SLACK_INVISIBLE_CHARACTER, " ")) as Block;
  }

  return block;
}

function fixMrkdwnOrTextType({
  type,
  text,
}: Extract<SlackEngineV2Message["data"][number], { type: "section" }>["text"]):
  | {
      type: "plain_text";
      text: string;
    }
  | {
      type: "mrkdwn";
      text: string;
    } {
  if (type === "plain_text") {
    return {
      type: "plain_text",
      text,
    };
  }

  return {
    type: "mrkdwn",
    text,
  };
}

const recurse = (
  obj: object,
  filter: (key: string, value: unknown) => boolean,
  callback: (obj: object) => void,
): void => {
  Object.entries(obj).map(([k, v]) =>
    typeof v === "object"
      ? recurse(v as object, filter, callback)
      : filter(k, v)
        ? callback(obj)
        : null,
  );
};
